import * as React from 'react';
import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Key,
  Copy,
  Check,
  RefreshCw,
  Download,
  Languages,
  Bot,
  // Zap, // removed: SkillTaskTimerConfig deprecated
  // FileText,
  ChevronDown,
  // X,
} from 'lucide-react';
import { apiGet, apiPost } from '@/lib/api';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useUIStore } from '@/stores';
// import { useSkillTaskStore, SKILL_TASK_STEPS } from '@/stores/skillTaskStore';
import AgentIcon from '@/components/AgentIcon';
import {
  fetchUserProfile,
  updateUserSettings,
  type UserSettings as UserSettingsType,
} from '@/services/userService';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import MobileHeader from '@/components/MobileHeader';
// import i18n from '@/i18n/config';

interface TokenInfo {
  id: number;
  token: string;
  expiredAt: string;
}

interface TokenResponse {
  code: number;
  message: string;
  tokens: TokenInfo[];
  baseUrl: string;
}

interface UserSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * User Settings Page Component
 * API Token 管理和语言切换设置
 */
const UserSettings = React.memo(function UserSettings({
  isOpen,
  onClose,
}: UserSettingsProps) {
  const { t } = useTranslation();
  const [tokenData, setTokenData] = useState<{
    baseUrl: string;
    tokens: TokenInfo[];
  } | null>(null);
  const [isLoadingTokens, setIsLoadingTokens] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState<string | null>(null);

  // Instructions modal state
  const [isInstructionsModalOpen, setIsInstructionsModalOpen] = useState(false);
  const [tempInstructions, setTempInstructions] = useState<string>('');
  const [originalInstructions, setOriginalInstructions] = useState<string>('');
  const [isSavingInstructions, setIsSavingInstructions] = useState(false);

  // Get agents from uiStore
  const { agents, fetchAgents, agentsLoaded } = useUIStore();

  // Custom dropdown state for default client
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  // User settings states
  const [userSettings, setUserSettings] = useState<UserSettingsType>({
    defaultModel: '',
    language: 'en-us',
    llmLanguage: 'en-us',
  });
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [showLLMLanguageDropdown, setShowLLMLanguageDropdown] = useState(false);
  const profileLoadedRef = React.useRef(false);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Close LLM language dropdown if clicking outside
      if (
        showLLMLanguageDropdown &&
        !target.closest('.llm-language-dropdown')
      ) {
        setShowLLMLanguageDropdown(false);
      }
    };

    if (showLLMLanguageDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showLLMLanguageDropdown]);

  // 动态跟踪侧边栏宽度
  // const [sidebarWidth, setSidebarWidth] = useState(() => {
  //   return localStorage.getItem('sidebarWidth') || '288';
  // });

  // 监听侧边栏宽度变化事件
  // useEffect(() => {
  //   // const handleSidebarWidthChange = (event: Event) => {
  //   //   const customEvent = event as CustomEvent;
  //   //   setSidebarWidth(String(customEvent.detail.width));
  //   // };

  //   // 添加事件监听器
  //   // window.addEventListener('sidebarWidthChange', handleSidebarWidthChange);

  //   // 清理函数
  //   // return () => {
  //   //   window.removeEventListener(
  //   //     'sidebarWidthChange',
  //   //     handleSidebarWidthChange
  //   //   );
  //   // };
  // }, []);

  // Fetch API tokens
  const fetchTokens = useCallback(async () => {
    setIsLoadingTokens(true);
    try {
      const response = await apiGet<TokenResponse>('/api/ai/cc/tokens');
      if (response && response.code === 200) {
        setTokenData({
          baseUrl: response.baseUrl || '',
          tokens: response.tokens || [],
        });
      }
    } catch (error) {
      console.error('Failed to fetch tokens:', error);
    } finally {
      setIsLoadingTokens(false);
    }
  }, []);

  // Load tokens when component opens
  useEffect(() => {
    if (isOpen) {
      fetchTokens();
    }
  }, [isOpen, fetchTokens]);

  // Fetch user profile on component mount
  useEffect(() => {
    const loadUserProfile = async () => {
      setIsLoadingProfile(true);
      try {
        const profile = await fetchUserProfile();
        if (profile && profile.settings) {
          setUserSettings(profile.settings);
          // Set initial instructions
          if (profile.settings.instructions) {
            setTempInstructions(profile.settings.instructions);
            setOriginalInstructions(profile.settings.instructions);
          }
          // Also update the global language settings
          if (profile.settings.language) {
            // Map language code: 'en-us' -> 'en', 'zh-cn' -> 'zh'
            const languageMap: { [key: string]: string } = {
              'en-us': 'en',
              'zh-cn': 'zh',
            };
            const mappedCode =
              languageMap[profile.settings.language] ||
              profile.settings.language;

            // Update i18n language to reflect the user's saved preference
            const { default: i18nInstance } = await import('@/i18n/config');
            i18nInstance.changeLanguage(mappedCode);
          }
        }
      } catch (error) {
        console.error('Failed to load user profile:', error);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    if (isOpen) {
      // 只在本次弹窗会话中首次加载 profile，防止 agentsLoaded 变化导致重复加载
      // 从而避免后端旧的语言设置覆盖用户刚切换的新语言
      if (!profileLoadedRef.current) {
        profileLoadedRef.current = true;
        loadUserProfile();
      }
      // Also load agents if not already loaded
      if (!agentsLoaded) {
        fetchAgents();
      }
    } else {
      // 弹窗关闭时重置，下次打开时重新加载
      profileLoadedRef.current = false;
    }
  }, [isOpen, agentsLoaded, fetchAgents]);

  // Save user settings to backend
  const saveUserSettings = useCallback(
    async (newSettings: Partial<UserSettingsType>) => {
      try {
        const updatedSettings = {
          ...userSettings,
          ...newSettings,
        };
        setUserSettings(updatedSettings);

        // 只发送变更的字段到后端，不发送整个对象
        const success = await updateUserSettings(newSettings);
        if (!success) {
          console.error('Failed to save user settings');
          // Optionally revert the state if save failed
        }
      } catch (error) {
        console.error('Error saving user settings:', error);
      }
    },
    [userSettings]
  );

  // Handle interface language change
  const handleInterfaceLanguageChange = useCallback(
    (language: string) => {
      saveUserSettings({ language });
    },
    [saveUserSettings]
  );

  // Handle LLM language change
  const handleLLMLanguageChange = useCallback(
    (llmLanguage: string) => {
      saveUserSettings({ llmLanguage });
    },
    [saveUserSettings]
  );

  // Handle save instructions
  const handleSaveInstructions = useCallback(async () => {
    setIsSavingInstructions(true);
    try {
      const trimmedInstructions = tempInstructions.trim();
      await saveUserSettings({ instructions: trimmedInstructions });
      setOriginalInstructions(trimmedInstructions); // Update original after successful save
      setTempInstructions(trimmedInstructions); // Update temp to trimmed version
      setIsInstructionsModalOpen(false);
    } catch (error) {
      console.error('Failed to save instructions:', error);
    } finally {
      setIsSavingInstructions(false);
    }
  }, [tempInstructions, saveUserSettings]);

  // Refresh all tokens
  const refreshAllTokens = useCallback(async () => {
    if (!tokenData?.tokens?.length) return;
    setIsLoadingTokens(true);
    try {
      await Promise.all(
        tokenData.tokens.map(token =>
          apiPost(`/api/ai/cc/token/${token.id}/refresh`, {})
        )
      );
      await fetchTokens();
    } catch (error) {
      console.error('Failed to refresh all tokens:', error);
    } finally {
      setIsLoadingTokens(false);
    }
  }, [tokenData?.tokens, fetchTokens]);

  // Copy to clipboard
  const copyToClipboard = useCallback(async (text: string, fieldId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldId);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  }, []);

  // Download settings file
  const downloadSettings = useCallback(
    async (tokenId: number, type: 'cc' | 'pi') => {
      setIsDownloading(`${tokenId}-${type}`);
      try {
        const endpoint =
          type === 'cc'
            ? `/api/ai/cc/tokens/${tokenId}/cc_settings/download`
            : `/api/ai/cc/tokens/${tokenId}/pi_settings/download`;

        const token = localStorage.getItem('user-token');
        const response = await fetch(endpoint, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = type === 'cc' ? 'settings.json' : 'models.json';
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          a.remove();
        }
      } catch (error) {
        console.error(`Failed to download ${type} settings:`, error);
      } finally {
        setIsDownloading(null);
      }
    },
    []
  );

  // 作为路由页面，不需要检查 isOpen
  // 直接返回普通的 React 组件，不使用 Portal
  return (
    <div className="h-full w-full bg-claude-bg flex flex-col">
      {/* Mobile Header */}
      <MobileHeader
        rightActions={
          <button
            onClick={onClose}
            className="p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={18} />
          </button>
        }
      >
        <h3 className="text-base font-bold truncate">
          {t('sidebar.userSettings')}
        </h3>
      </MobileHeader>
      {/* Desktop 顶部标题栏 - 移到滚动容器外部，确保覆盖全宽 */}
      <div className="flex-shrink-0 z-10 bg-white border-b border-gray-200 px-6 py-1 hidden lg:flex items-center justify-between w-full">
        <div className="flex items-center gap-1">
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <h3 className="text-base font-bold">{t('sidebar.userSettings')}</h3>
        </div>
      </div>
      {/* 主内容区域 */}
      <div className="flex-1 min-h-0 overflow-auto bg-claude-bg">
        <div className="p-6 max-w-4xl mx-auto space-y-6">
          {/* 语言设置部分 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Languages size={20} className="text-gray-700" />
              <h2 className="text-lg font-semibold">
                {t('userSettings.language.title')}
              </h2>
            </div>

            {/* 界面语言 */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                {t(
                  'userSettings.interfaceLanguage.title',
                  'Interface Language'
                )}
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                {t(
                  'userSettings.interfaceLanguage.description',
                  'Choose the language for the user interface'
                )}
              </p>
              <div className="max-w-xs">
                <LanguageSwitcher
                  className="w-full justify-center"
                  showLabel={true}
                  onLanguageChange={handleInterfaceLanguageChange}
                />
              </div>
            </div>

            {/* LLM 语言 */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                {t('userSettings.llmLanguage.title', 'AI Model Language')}
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                {t(
                  'userSettings.llmLanguage.description',
                  'Choose the language for AI model responses'
                )}
              </p>
              <div className="max-w-xs">
                <div className="relative llm-language-dropdown">
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                    onClick={() =>
                      setShowLLMLanguageDropdown(!showLLMLanguageDropdown)
                    }
                    disabled={isLoadingProfile}
                  >
                    <Languages size={18} />
                    <span className="font-medium">
                      {userSettings.llmLanguage === 'zh-cn'
                        ? '中文'
                        : 'English'}
                    </span>
                    <ChevronDown
                      size={16}
                      className={`transition-transform ${showLLMLanguageDropdown ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {showLLMLanguageDropdown && (
                    <div className="absolute left-0 top-10 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                      <div className="py-1">
                        <button
                          type="button"
                          className={`w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${
                            userSettings.llmLanguage === 'en-us'
                              ? 'bg-gray-50 text-blue-600'
                              : 'text-gray-700'
                          }`}
                          onClick={() => {
                            handleLLMLanguageChange('en-us');
                            setShowLLMLanguageDropdown(false);
                          }}
                        >
                          <span className="font-medium">English</span>
                          {userSettings.llmLanguage === 'en-us' && (
                            <span className="ml-auto">✓</span>
                          )}
                        </button>
                        <button
                          type="button"
                          className={`w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${
                            userSettings.llmLanguage === 'zh-cn'
                              ? 'bg-gray-50 text-blue-600'
                              : 'text-gray-700'
                          }`}
                          onClick={() => {
                            handleLLMLanguageChange('zh-cn');
                            setShowLLMLanguageDropdown(false);
                          }}
                        >
                          <span className="font-medium">中文</span>
                          {userSettings.llmLanguage === 'zh-cn' && (
                            <span className="ml-auto">✓</span>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 默认客户端和指令设置部分 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Bot size={20} className="text-gray-700" />
              <h2 className="text-lg font-semibold">
                {t('userSettings.defaultClientAndInstructions')}
              </h2>
            </div>

            {/* 默认客户端选择 */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                {t('userSettings.defaultClient.title')}
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                {t('userSettings.defaultClient.description')}
              </p>
              <div className="max-w-xs">
                <div className="relative">
                  {/* Custom dropdown trigger */}
                  <button
                    type="button"
                    onClick={() => setShowClientDropdown(!showClientDropdown)}
                    disabled={!agentsLoaded || isLoadingProfile}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {userSettings?.defaultClientId ? (
                      <>
                        <AgentIcon
                          agent={agents.find(
                            a => a.id === userSettings.defaultClientId
                          )}
                          className="w-5 h-5 rounded flex-shrink-0"
                        />
                        <span className="flex-1 text-left truncate">
                          {agents.find(
                            a => a.id === userSettings.defaultClientId
                          )?.name || userSettings.defaultClientId}
                        </span>
                      </>
                    ) : (
                      <span className="flex-1 text-left text-gray-500">
                        {t('userSettings.defaultClient.placeholder')}
                      </span>
                    )}
                    <ChevronDown
                      size={16}
                      className={`transition-transform ${showClientDropdown ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {/* Dropdown menu */}
                  {showClientDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                      {/* Default option */}
                      {/* <button
                        type="button"
                        onClick={() => {
                          saveUserSettings({ defaultClientId: '' });
                          setShowClientDropdown(false);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left"
                      >
                        <div className="w-5 h-5 flex-shrink-0" />
                        <span className="text-gray-500">
                          {t('userSettings.defaultClient.placeholder')}
                        </span>
                      </button> */}

                      {/* Agent options */}
                      {agents.map(agent => (
                        <button
                          key={agent.id}
                          type="button"
                          onClick={() => {
                            saveUserSettings({ defaultClientId: agent.id });
                            setShowClientDropdown(false);
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left ${
                            userSettings?.defaultClientId === agent.id
                              ? 'bg-blue-50'
                              : ''
                          }`}
                        >
                          <AgentIcon
                            agent={agent}
                            className="w-5 h-5 rounded flex-shrink-0"
                          />
                          <span className="flex-1 truncate">{agent.name}</span>
                          {userSettings?.defaultClientId === agent.id && (
                            <Check
                              size={16}
                              className="text-blue-600 flex-shrink-0"
                            />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 用户指令 */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                {t('userSettings.instructions.title')}
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                {t('userSettings.instructions.description')}
              </p>
              <button
                onClick={() => {
                  const currentInstructions = userSettings?.instructions || '';
                  setTempInstructions(currentInstructions);
                  setOriginalInstructions(currentInstructions);
                  setIsInstructionsModalOpen(true);
                }}
                className="px-4 py-2 text-sm bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                {userSettings?.instructions
                  ? t('userSettings.instructions.edit')
                  : t('userSettings.instructions.add')}
              </button>
              {userSettings?.instructions && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap line-clamp-3">
                    {userSettings.instructions}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* 🔥 Skill Task 步骤时长配置（已移除，数据从 API + WS 获取） */}

          {/* API Token 部分 - 从原来的 Modal 内容复制过来 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Key size={20} className="text-gray-700" />
              <h2 className="text-lg font-semibold">{t('sidebar.apiToken')}</h2>
            </div>

            <div className="space-y-4">
              {isLoadingTokens ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span className="ml-2 text-gray-500">
                    {t('common.loading')}
                  </span>
                </div>
              ) : tokenData ? (
                <>
                  {/* Base URL */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('sidebar.baseUrl')}
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={tokenData.baseUrl}
                        readOnly
                        className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 font-mono"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          copyToClipboard(tokenData.baseUrl, 'baseUrl')
                        }
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title={t('sidebar.copyBaseUrl')}
                      >
                        {copiedField === 'baseUrl' ? (
                          <Check size={16} className="text-green-500" />
                        ) : (
                          <Copy size={16} className="text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Tokens */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('sidebar.tokens')}
                    </label>
                    <div className="space-y-3">
                      {tokenData.tokens.map((token, index) => (
                        <div
                          key={token.id}
                          className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <input
                              type="text"
                              value={token.token}
                              readOnly
                              className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 font-mono"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                copyToClipboard(token.token, `token-${index}`)
                              }
                              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                              title={t('sidebar.copyToken')}
                            >
                              {copiedField === `token-${index}` ? (
                                <Check size={16} className="text-green-500" />
                              ) : (
                                <Copy size={16} className="text-gray-500" />
                              )}
                            </button>
                          </div>
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center justify-between text-xs">
                              <span
                                className={
                                  Date.parse(token.expiredAt) < Date.now()
                                    ? 'text-red-500 font-medium'
                                    : 'text-gray-500'
                                }
                              >
                                {t('sidebar.expires')}:{' '}
                                {new Date(token.expiredAt).toLocaleString()}
                              </span>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    downloadSettings(token.id, 'cc')
                                  }
                                  disabled={isDownloading === `${token.id}-cc`}
                                  className="flex items-center gap-1 px-2 py-1 text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                                >
                                  <Download
                                    size={12}
                                    className={
                                      isDownloading === `${token.id}-cc`
                                        ? 'animate-bounce'
                                        : ''
                                    }
                                  />
                                  <span>{t('sidebar.ccSetting')}</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    downloadSettings(token.id, 'pi')
                                  }
                                  disabled={isDownloading === `${token.id}-pi`}
                                  className="flex items-center gap-1 px-2 py-1 text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                                >
                                  <Download
                                    size={12}
                                    className={
                                      isDownloading === `${token.id}-pi`
                                        ? 'animate-bounce'
                                        : ''
                                    }
                                  />
                                  <span>{t('sidebar.piSetting')}</span>
                                </button>
                              </div>
                            </div>
                            {Date.parse(token.expiredAt) < Date.now() && (
                              <span className="text-xs text-red-500">
                                {t('sidebar.tokenExpired')}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                      {tokenData.tokens.length === 0 && (
                        <div className="text-center py-4 text-gray-500 text-sm">
                          {t('sidebar.noTokens')}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 刷新按钮 */}
                  <div className="flex justify-end pt-4">
                    <button
                      type="button"
                      onClick={refreshAllTokens}
                      disabled={isLoadingTokens || !tokenData?.tokens?.length}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RefreshCw
                        size={16}
                        className={isLoadingTokens ? 'animate-spin' : ''}
                      />
                      <span>{t('sidebar.refresh')}</span>
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {t('sidebar.failedToLoadTokens')}
                </div>
              )}
            </div>

            {/* 安装说明 */}
            <div className="mt-6 p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-gray-700">
              <p className="font-medium mb-2 text-blue-800">
                {t('sidebar.instructions')}:
              </p>
              <ul className="space-y-2">
                <li>
                  <strong>{t('sidebar.claudeCodeSetting')}:</strong>
                  <code className="text-xs bg-white px-2 py-1 rounded border border-gray-200 ml-2 break-all inline-block mt-1">
                    C:\Users\your_account\.claude\settings.json
                  </code>
                </li>
                <li>
                  <strong>{t('sidebar.piMonoSetting')}:</strong>
                  <code className="text-xs bg-white px-2 py-1 rounded border border-gray-200 ml-2 break-all inline-block mt-1">
                    C:\Users\your_account\.pi\agent\models.json
                  </code>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Instructions Modal */}
      <Dialog
        open={isInstructionsModalOpen}
        onOpenChange={setIsInstructionsModalOpen}
      >
        <DialogContent className="sm:max-w-2xl" style={{ zIndex: 10000 }}>
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              {t('userSettings.instructions.modalTitle', '设置用户指令')}
            </DialogTitle>
            <p className="text-sm text-gray-600 mt-2">
              {t(
                'userSettings.instructions.modalDescription',
                '添加个人化的指令，AI 将根据这些指令来生成更符合您需求的响应。'
              )}
            </p>
          </DialogHeader>
          <div className="px-6 py-4">
            <textarea
              value={tempInstructions}
              onChange={e => setTempInstructions(e.target.value)}
              placeholder={t(
                'userSettings.instructions.placeholder',
                '例如：请用简洁的语言回答，避免冗长的解释...'
              )}
              className="w-full h-64 p-4 text-sm border border-gray-400 rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-blue-300 bg-white"
              autoFocus
            />
          </div>
          <DialogFooter className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsInstructionsModalOpen(false)}
              disabled={isSavingInstructions}
              className="px-4 py-2"
            >
              {t('common.cancel', '取消')}
            </Button>
            <Button
              type="button"
              onClick={handleSaveInstructions}
              disabled={
                isSavingInstructions ||
                tempInstructions.trim() === originalInstructions.trim()
              }
              className="px-4 py-2 bg-gray-800 text-white hover:bg-gray-700 disabled:opacity-50"
            >
              {isSavingInstructions ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b border-white"></div>
                  {t('common.saving', '保存中')}
                </span>
              ) : (
                t('common.save', '保存')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

// SkillTaskTimerConfig - 已废弃（数据从 API + WS 获取，不再需要本地模拟配置）

export default UserSettings;
